import React, { useState, forwardRef, useImperativeHandle, useMemo, useEffect, useRef } from "react";
import { CountryCode, countryCodes, defaultPhoneCountry } from "./country-codes";
import "@/features/auth/components/login-form/form.css";
import Image from "next/image";
import { useTranslations } from "next-intl";
import UserIcon from "@/features/user-center/components/user-icon";
import './index.css';
import clsx from "clsx";

// Input field types
export type InputType = 'email' | 'verification' | 'phone' | 'password' | 'text' | 'number';

// Ref interface for input field access
export interface InputFieldRef {
  getValue: () => string;
  setValue: (value: string) => void;
  focus: () => void;
  blur: () => void;
}

// Input mode types for different keyboard layouts
export type InputMode = 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search';

// Props interface for the reusable input component
interface InputFieldProps {
  type: InputType;
  value: string;
  onChange: (value: string) => void;
  validator?: (value: string) => string; // 自定义检验方法
  onChangeBefore?: (value: string) => void; // 修改前置，常用于处理错误清空
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  showStatus?: boolean;
  statusText?: string;
  onSendAction?: () => void;
  resendCountdown?: number;
  className?: string;
  inputClassName?: string;
  errorClassName?: string;
  rightContainerClassName?: string;
  maxLength?: number; // 最大输入长度限制
  showMaxLengthError?: boolean;
  inputMode?: InputMode; // 控制移动设备键盘类型
  // Phone-specific props
  selectedCountryCode?: CountryCode;
  onCountryCodeChange?: (countryCode: CountryCode) => void;
  // Verification code specific props
  hasSentCode?: boolean; // Whether verification code has been sent initially
  // Custom button props
  rightElement?: React.ReactNode; // 添加自定义右侧元素支持
  suffixIcon?: React.ReactNode;
}

// Reusable Input Field Component
const InputField = forwardRef<InputFieldRef, InputFieldProps>(({
  type,
  value,
  onChange,
  onChangeBefore,
  onBlur,
  placeholder,
  disabled = false,
  error,
  showStatus = false,
  statusText,
  onSendAction,
  resendCountdown = 0,
  className = "",
  selectedCountryCode = defaultPhoneCountry.code,
  onCountryCodeChange,
  hasSentCode = false,
  rightElement,
  inputClassName = "",
  errorClassName = "",
  maxLength,
  showMaxLengthError = false,
  inputMode,
  suffixIcon,
  validator,
  rightContainerClassName,
}, ref) => {
  const t = useTranslations('personal');
  const authT = useTranslations('auth');
  const [showPassword, setShowPassword] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [internalError, setInternalError] = useState<string>('');
  const [isMaxLengthError, setIsMaxLengthError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // 获取当前显示的错误（外部传入的错误优先）
  const currentError = useMemo(() => error || internalError, [error, internalError]);

  // 自动清除长度错误提示
  useEffect(() => {
    if (isMaxLengthError) {
      const timer = setTimeout(() => {
        setInternalError('');
        setIsMaxLengthError(false);
      }, 3000); // 3秒后自动清除

      return () => clearTimeout(timer);
    }
  }, [isMaxLengthError]);

  // 暴露给外部的方法
  useImperativeHandle(ref, () => ({
    getValue: () => value,
    setValue: (newValue: string) => {
      onChange(newValue);
      // 聚焦输入框
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    },
    focus: () => inputRef.current?.focus(),
    blur: () => inputRef.current?.blur()
  }), [value, onChange]);

  // Get input type for HTML input element
  const getInputType = () => {
    switch (type) {
      case 'email':
        return 'email';
      case 'phone':
        return 'tel';
      case 'password':
        return showPassword ? 'text' : 'password';
      case 'verification':
        return 'text';
      default:
        return 'text';
    }
  };

  // Get placeholder text
  const getPlaceholder = () => {
    if (placeholder) return placeholder;

    const placeholderMap: Record<string, string> = {
      email: t('form.validation.pleaseEnterEmailAddress'),
      phone: t('form.validation.pleaseEnterPhoneNumber'),
      password: t('form.validation.pleaseEnterPassword'),
      verification: authT('verification.title'),
    };
    return placeholderMap[type] ?? '';
  };

  // Get input validation pattern
  const getPattern = () => {
    const patternMap: Record<string, string | undefined> = {
      email: "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$",
      phone: "^1[3-9]\\d{9}$", // Chinese mobile number pattern
      verification: "^\\d{4,6}$", // 4-6 digit verification code
    };
    return patternMap[type] ?? undefined;
  };

  // Get input mode for mobile keyboard
  const getInputMode = (): InputMode => {
    // 如果明确传入了 inputMode，则使用传入的值
    if (inputMode) return inputMode;

    // 否则根据 type 自动判断
    const inputModeMap: Record<string, InputMode> = {
      email: 'email',
      phone: 'tel',
      verification: 'numeric',
      password: 'text',
      text: 'text',
    };
    return inputModeMap[type] ?? 'text';
  };
  const SuffixIconMap = {
    email: <UserIcon name="mail" alt='email' width={20} height={20} />,
    verification: <UserIcon name="captcha" alt='verification' width={20} height={20} />,
    password: <UserIcon name="lock" alt='password' width={20} height={20} />,
  };

  const selectedCountry = useMemo(() => countryCodes.find(country => country.code === selectedCountryCode) || defaultPhoneCountry, [selectedCountryCode]);

  const handleClear = () => {
    onChange('');
    // 清除长度限制错误
    if (isMaxLengthError) {
      setInternalError('');
      setIsMaxLengthError(false);
    }
    // 重新聚焦输入框
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const ClearBtn = <UserIcon name="close" alt='close' className="w-2.5 h-2.5 cursor-pointer" width={10} height={10} onClick={handleClear} />;
  return (
    <div className={`flex flex-col relative ${className}`}>
      {/* Status Display (for bound email, etc.) */}
      {showStatus && (
        <>
          <div className={`flex items-center justify-between w-[282px] h-[45px] border-2 ${currentError ? 'border-accent-red' : 'border-accent-blue'} rounded-lg px-[37px] py-[17px] mb-1 bg-[rgba(102,114,132,0)]`}>
            <div className="text-white text-[13px] font-medium whitespace-nowrap">
              {value || statusText}
            </div>
            <div className="w-0.5 h-3 bg-accent-blue rounded-sm" />
          </div>

          {/* Status Text */}
          {statusText && (
            <div className={`${currentError ? 'text-accent-red' : 'text-accent-blue'} text-[10px] font-semibold leading-[9px] mb-3`}>
              {statusText}
            </div>
          )}
        </>
      )}

      {/* Input Field */}
      <div className="relative flex items-center h-full">
        {/* Country Code Selector for Phone Input */}
        {type === 'phone' && (
          <div className="absolute h-full left-[11px] flex items-center text-white text-[13px] font-medium z-10">
            <button
              type="button"
              // onClick={() => setShowCountryDropdown(!showCountryDropdown)}
              className="flex items-center gap-1 hover:opacity-80 transition-opacity"
            >
              <Image src={selectedCountry.icon} className="w-[15px] h-[15px]" alt={selectedCountry.country} width={15} height={15} />
              <span className="text-xs text-white font-medium flex-1">{selectedCountry.country} {selectedCountry.code}</span>
              {/* <UserIcon name="more" className={`h-2.5 w-1.5 transition-transform ${showCountryDropdown ? 'rotate-[270deg]' : 'rotate-90'}`} /> */}
              {/* <Image  src={RightIcon} className={`h-2.5 w-1.5 transition-transform ${showCountryDropdown ? 'rotate-[270deg]' : 'rotate-90'}`} alt="arrow-down" width={6} height={10} /> */}

            </button>

            {/* Country Code Dropdown */}
            {showCountryDropdown && (
              <>
                {/* Backdrop */}
                {/* <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowCountryDropdown(false)}
                /> */}

                {/* Dropdown Menu */}
                <div className="absolute top-full mt-2 left-[-11px] z-20 w-[130%] max-h-[240px] bg-[#1a1f2e] border border-[rgba(214,234,255,0.16)] rounded-lg shadow-lg overflow-y-auto">
                  {countryCodes.map((country) => (
                    <button
                      key={country.code}
                      type="button"
                      onClick={() => {
                        onCountryCodeChange?.(country);
                        setShowCountryDropdown(false);
                      }}
                      className={`w-full flex flex-nowrap items-center px-3 py-2 text-left hover:bg-[rgba(102,114,132,0.2)] transition-colors ${selectedCountryCode === country.code ? 'bg-[rgba(0,187,255,0.1)]' : ''
                        }`}
                    >
                      <Image src={country.icon} className="w-[15px] h-[15px] mr-1" alt={country.country} width={15} height={15} />
                      <span className="text-xs text-white font-medium flex-1">{country.country} {country.code}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        <div className="relative w-full flex items-center h-full">
          {/* 可选的前置图标 */}
          {SuffixIconMap[type] && (
            <span className="absolute left-3 flex items-center pointer-events-none">
              {SuffixIconMap[type]}
            </span>
          )}
          {suffixIcon && (
            <span className="absolute left-3 flex items-center pointer-events-none">
              {suffixIcon}
            </span>
          )}
          <input
            data-keyboard-fix
            ref={inputRef}
            type={getInputType()}
            inputMode={getInputMode()}
            value={value}
            onChange={(e) => {
              const newValue = e.target.value;
              if (typeof onChangeBefore === 'function') {
                onChangeBefore(newValue);
              }
              setInternalError('');
              // 判断是否符合自定义检验，如有错误，返回错误提示
              const inValidMsg = typeof validator === 'function' && validator(newValue);
              if (inValidMsg) {
                setInternalError(inValidMsg);
                onChange(newValue);
                return;
              }
              // 如果设置了最大长度限制，则截断超出部分
              if (maxLength && newValue.length > maxLength) {
                // 显示错误提示
                if (showMaxLengthError) {
                  setInternalError(t('form.validation.upToLengthCharacters', { length: maxLength }));
                  setIsMaxLengthError(true);
                }
                // 保持在最大长度
                onChange(newValue.slice(0, maxLength));
              } else {
                // 清除长度限制错误提示
                if (isMaxLengthError) {
                  setInternalError('');
                  setIsMaxLengthError(false);
                }
                onChange(newValue);
              }
            }}
            onBlur={onBlur}
            placeholder={getPlaceholder()}
            pattern={getPattern()}
            disabled={disabled}
            className={`w-full h-full comInput ${type === 'phone' ? '!pl-[85px] pr-[40px]' : ''} ${SuffixIconMap[type] ? '!pl-10' : ''} ${
              // 根据右侧元素类型动态调整右侧padding
              resendCountdown > 0 ? 'pr-[70px]' :
                type === 'verification' && onSendAction ? 'pr-[70px]' :
                  (value && !disabled && type !== 'verification') || type === 'password' || rightElement ? '!pr-[30px]' : ''
              } ${currentError ? 'errorOutline' : ''} ${inputClassName}`}
          />
        </div>

        {/* Right side icons/actions */}
        <div className={clsx('absolute right-[11px] top-1/2 transform -translate-y-1/2 flex items-center gap-2', rightContainerClassName)}>
          {/* Send/Resend button for verification code */}
          {type === 'verification' && onSendAction && (
            <div className="flex items-center">
              <div className="w-[1px] h-3 bg-tertiary mr-[.4375rem]"></div>
              <button
                type="button"
                onClick={() => {
                  onSendAction();
                  // 自动聚焦到 input
                  setTimeout(() => {
                    inputRef.current?.focus();
                  }, 0);
                }}
                disabled={resendCountdown > 0 || disabled}
                className={`min-w-[23px] font-medium text-xs ${resendCountdown > 0 || disabled ? 'text-tertiary' : 'text-[#00B8FB] active:scale-90'}`}
              >
                {resendCountdown > 0
                  ? authT('verification.resendCountdown', { seconds: resendCountdown })
                  : hasSentCode
                    ? authT('verification.resendNow')
                    : t('send')
                }
              </button>
            </div>
          )}

          {/* Clear button when input has value */}
          {value && !disabled && type !== 'verification' && (
            <div className="flex items-center h-full">
              {ClearBtn}
            </div>
          )}
          {/* Password visibility toggle */}
          {type === 'password' && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-white hover:opacity-80 transition-opacity active:scale-90"
            >
              {showPassword ? (
                <UserIcon name="pwd-on" alt='password' width={20} height={20} />
              ) : (
                <UserIcon name="pwd-off" alt='password' width={20} height={20} />
              )}
            </button>
          )}

          {/* Custom right element */}
          {rightElement && (
            <div className="flex items-center h-full">
              {rightElement}
            </div>
          )}
        </div>
      </div>

      {/* Error message */}
      {currentError && (
        <div className={`absolute bottom-0 translate-y-full left-0 errorText text-accent-red font-semibold text-[10px] ${errorClassName}`}>
          {currentError}
        </div>
      )}
    </div>
  );
});

InputField.displayName = 'InputField';

// Example usage components for different input types

// Email Input with Status
export const EmailInputWithStatus: React.FC<{
  email: string;
  onEmailChange: (email: string) => void;
  isBound?: boolean;
  error?: string;
  className?: string;
  inputClassName?: string;
  errorClassName?: string;
  maxLength?: number;
  inputMode?: InputMode;
}> = ({ email, onEmailChange, isBound = false, error, className, inputClassName, errorClassName, maxLength, inputMode }) => {
  const authT = useTranslations('auth');
  return (
    <InputField
      type="email"
      value={email}
      onChange={onEmailChange}
      showStatus={isBound}
      statusText={isBound ? authT('validation.emailAlreadyBound') : undefined}
      error={error}
      className={className}
      inputClassName={inputClassName}
      errorClassName={errorClassName}
      maxLength={maxLength}
      inputMode={inputMode}
    />
  );
};

// Verification Code Input
export const VerificationCodeInput = forwardRef<InputFieldRef, {
  code: string;
  onCodeChange: (code: string) => void;
  onResend: () => void;
  countdown: number;
  error?: string;
  hasSentCode?: boolean;
  className?: string;
  inputClassName?: string;
  errorClassName?: string;
  maxLength?: number;
  inputMode?: InputMode;
  disabled?: boolean;
}>(({ code, onCodeChange, onResend, countdown, error, hasSentCode = false, className, inputClassName, errorClassName, maxLength = 6, inputMode, disabled }, ref) => (
  <InputField
    ref={ref}
    type="verification"
    value={code}
    onChange={onCodeChange}
    onSendAction={onResend}
    resendCountdown={countdown}
    error={error}
    hasSentCode={hasSentCode}
    className={className}
    inputClassName={inputClassName}
    errorClassName={errorClassName}
    maxLength={maxLength}
    inputMode={inputMode}
    disabled={disabled}
  />
));

VerificationCodeInput.displayName = 'VerificationCodeInput';

// Phone Number Input
export const PhoneNumberInput: React.FC<{
  phone: string;
  onPhoneChange: (phone: string) => void;
  selectedCountryCode?: CountryCode;
  onCountryCodeChange?: (countryCode: CountryCode) => void;
  error?: string;
  className?: string;
  inputClassName?: string;
  errorClassName?: string;
  maxLength?: number;
  inputMode?: InputMode;
}> = ({ phone, onPhoneChange, selectedCountryCode, onCountryCodeChange, error, className, inputClassName, errorClassName, maxLength, inputMode }) => (
  <InputField
    type="phone"
    value={phone}
    onChange={onPhoneChange}
    selectedCountryCode={selectedCountryCode}
    onCountryCodeChange={onCountryCodeChange}
    error={error}
    className={className}
    inputClassName={inputClassName}
    errorClassName={errorClassName}
    maxLength={maxLength}
    inputMode={inputMode}
  />
);

// Password Input
export const PasswordInput: React.FC<{
  password: string;
  onPasswordChange: (password: string) => void;
  placeholder?: string;
  error?: string;
  className?: string;
  inputClassName?: string;
  errorClassName?: string;
  maxLength?: number;
  showMaxLengthError?: boolean;
  inputMode?: InputMode;
}> = ({ password, onPasswordChange, placeholder, error, className, inputClassName, errorClassName, maxLength, showMaxLengthError, inputMode }) => (
  <InputField
    type="password"
    value={password}
    onChange={onPasswordChange}
    placeholder={placeholder}
    error={error}
    className={className}
    inputClassName={inputClassName}
    errorClassName={errorClassName}
    maxLength={maxLength}
    showMaxLengthError={showMaxLengthError}
    inputMode={inputMode}
  />
);

export default InputField;
